import React from 'react';
import styled, { css } from 'styled-components';
import {
    space,
    layout,
    typography,
    color,
    position,
    type SpaceProps,
    type LayoutProps,
    type TypographyProps,
    type ColorProps,
    type PositionProps,
} from 'styled-system';
import { breakpointUp } from 'styles/breakpoints';
import { getValueAndUnit } from 'polished';
import Markdown from '../markdown/Markdown';

/** Limit to HTML tags you actually support (no SVG), which avoids SVG overloads */
type AllowedTag = 'p' | 'span' | 'strong' | 'em' | 'i' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'div' | 'label' | 'small';

type HeadingFlags = { h1?: boolean; h2?: boolean; h3?: boolean; h4?: boolean; h5?: boolean };
type WeightFlags = { light?: boolean; regular?: boolean; medium?: boolean; bold?: boolean };
type IntentFlags = { primary?: boolean; secondary?: boolean; danger?: boolean; warning?: boolean };
type RoleFlags = {
    lead?: boolean;
    playback?: boolean;
    body?: boolean;
    small?: boolean;
    primaryValue?: boolean;
    secondaryValue?: boolean;
    p?: boolean;
};

export type TextOwnProps = HeadingFlags &
    WeightFlags &
    IntentFlags &
    RoleFlags & {
        /** Which HTML tag to render (don’t use `as`) */
        tag?: AllowedTag;
        /** Render children as Markdown text */
        markdown?: boolean;
        children?: React.ReactNode;
        className?: string;
    };

/** Include native HTML attributes so id/aria-* etc. are allowed */
export type TextProps = TextOwnProps &
    SpaceProps &
    LayoutProps &
    TypographyProps &
    ColorProps &
    PositionProps &
    React.HTMLAttributes<HTMLElement> & {
        /** Disallow `as` to keep control in `tag` */
        as?: never;
    };

const inlineTags = ['span', 'strong', 'em', 'i'] as const;
type BreakKey = 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | null;

/** Base render component that strips style-only flags so they aren't forwarded to the DOM */
const BaseText: React.FC<TextProps> = props => {
    const {
        // strip boolean styling flags so they don't hit the DOM
        h1,
        h2,
        h3,
        h4,
        h5,
        light,
        regular,
        medium,
        bold,
        primary,
        secondary,
        danger,
        warning,
        lead,
        playback,
        body,
        small,
        primaryValue,
        secondaryValue,
        p,

        // control/rendering
        tag = 'p',
        markdown,

        // keep everything else (id, aria-*, className, styled-system props, etc.)
        children,
        // remove potential `as` to avoid conflicting with our `tag`
        as: _as,
        ...rest
    } = props;

    const allowedTags: AllowedTag[] = [
        'p',
        'span',
        'strong',
        'em',
        'i',
        'h1',
        'h2',
        'h3',
        'h4',
        'h5',
        'div',
        'label',
        'small',
    ];
    const Tag = allowedTags.includes(tag as AllowedTag) ? tag : 'p';

    return markdown
        ? React.createElement(
              Tag,
              { ...rest },
              <Markdown removeWrapper>{typeof children === 'string' ? children : ''}</Markdown>
          )
        : React.createElement(Tag, { ...rest }, children);
};

const isNotInlineTag = (props: TextProps) =>
    !props.tag || !inlineTags.includes(props.tag as (typeof inlineTags)[number]);

// Your theme should provide fontSizes[], fontWeights, lineHeights,
// colors, and optionally headings.marginTop/marginBottom, paragraphs.marginTop/marginBottom.
const getTextStyle = (breakpoint: BreakKey, props: TextProps & { theme: any }) => {
    let fontSize: string | number | undefined;
    let fontWeight: string | number | undefined;
    let lineHeight: string | number | undefined;
    let fontColor: string | undefined;

    // Inline tags: inherit sizing/line-height; strong = bold
    if (props.tag && inlineTags.includes(props.tag as any)) {
        fontSize = 'inherit';
        lineHeight = 'inherit';
        fontWeight = props.tag === 'strong' ? props.theme?.fontWeights?.bold : 'inherit';
        fontColor = 'inherit';
        return css`
            font-size: ${fontSize};
            font-weight: ${fontWeight};
            line-height: ${lineHeight};
            color: ${fontColor};
        `;
    }

    // Helpers with safe fallbacks
    const fs = props.theme?.fontSizes ?? ['16px'];
    const fw = props.theme?.fontWeights ?? { light: 300, regular: 400, medium: 500, bold: 700 };
    const idx = (i: number) => fs[Math.min(i, fs.length - 1)]; // clamp indices to avoid undefined

    // Font size by flags + breakpoint (ported 1:1, but clamped)
    const pickSize = (b: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'default') => {
        switch (b) {
            case 'xs':
                if (props.lead) return idx(4);
                if (props.playback) return idx(3);
                if (props.body) return idx(3);
                if (props.small) return idx(3);
                if (props.primaryValue) return idx(8);
                if (props.secondaryValue) return idx(6);
                if (props.h1) return idx(10);
                if (props.h2) return idx(8);
                if (props.h3) return idx(6);
                if (props.h4) return idx(5);
                if (props.h5) return idx(4);
                return idx(3);
            case 'sm':
                if (props.lead) return idx(5);
                if (props.playback) return idx(4);
                if (props.body) return idx(4);
                if (props.small) return idx(3);
                if (props.primaryValue) return idx(8);
                if (props.secondaryValue) return idx(6);
                if (props.h1) return idx(10);
                if (props.h2) return idx(8);
                if (props.h3) return idx(6);
                if (props.h4) return idx(5);
                if (props.h5) return idx(4);
                return idx(4);
            case 'md':
                if (props.lead) return idx(5);
                if (props.playback) return idx(4);
                if (props.body) return idx(4);
                if (props.small) return idx(3);
                if (props.primaryValue) return idx(9);
                if (props.secondaryValue) return idx(7);
                if (props.h1) return idx(11);
                if (props.h2) return idx(9);
                if (props.h3) return idx(7);
                if (props.h4) return idx(6);
                if (props.h5) return idx(5);
                return idx(4);
            case 'lg':
                if (props.lead) return idx(5);
                if (props.playback) return idx(4);
                if (props.body) return idx(4);
                if (props.small) return idx(3);
                if (props.primaryValue) return idx(10);
                if (props.secondaryValue) return idx(8);
                if (props.h1) return idx(12);
                if (props.h2) return idx(10);
                if (props.h3) return idx(8);
                if (props.h4) return idx(7);
                if (props.h5) return idx(6);
                return idx(4);
            case 'xl':
                if (props.lead) return idx(5);
                if (props.playback) return idx(4);
                if (props.body) return idx(4);
                if (props.small) return idx(3);
                if (props.primaryValue) return idx(10);
                if (props.secondaryValue) return idx(8);
                if (props.h1) return idx(12);
                if (props.h2) return idx(10);
                if (props.h3) return idx(8);
                if (props.h4) return idx(7);
                if (props.h5) return idx(6);
                return idx(4);
            default:
                if (props.lead) return idx(4);
                if (props.playback) return idx(3);
                if (props.body) return idx(3);
                if (props.small) return idx(3);
                if (props.primaryValue) return idx(8);
                if (props.secondaryValue) return idx(6);
                if (props.h1) return idx(10);
                if (props.h2) return idx(8);
                if (props.h3) return idx(6);
                if (props.h4) return idx(5);
                if (props.h5) return idx(4);
                return idx(3);
        }
    };

    if (breakpoint === 'xs') fontSize = pickSize('xs');
    else if (breakpoint === 'sm') fontSize = pickSize('sm');
    else if (breakpoint === 'md') fontSize = pickSize('md');
    else if (breakpoint === 'lg') fontSize = pickSize('lg');
    else if (breakpoint === 'xl') fontSize = pickSize('xl');
    else fontSize = pickSize('default');

    // Weight
    if (props.light) fontWeight = fw.light;
    else if (props.regular) fontWeight = fw.regular;
    else if (props.medium) fontWeight = fw.medium;
    else if (props.bold) fontWeight = fw.bold;
    else if (props.lead) fontWeight = fw.medium;
    else if (props.playback) fontWeight = fw.regular;
    else if (props.body) fontWeight = fw.regular;
    else if (props.small) fontWeight = fw.light;
    else if (props.primaryValue) fontWeight = fw.light;
    else if (props.secondaryValue) fontWeight = fw.light;
    else if (props.h1 || props.h2 || props.h3 || props.h4 || props.h5) fontWeight = fw.bold;
    else fontWeight = fw.regular;

    // Line height
    const lh = props.theme?.lineHeights ?? { copy: 1.4, title: 1.3 };
    if (props.h1 || props.h2 || props.h3 || props.h4 || props.h5) lineHeight = lh.title;
    else lineHeight = lh.copy;

    // Color (guard missing palette keys)
    const c = props.theme?.colors ?? {};
    if (props.primary) fontColor = c.primaryText;
    else if (props.secondary) fontColor = c.secondaryText;
    else if (props.danger && c.danger) fontColor = c.danger;
    else if (props.warning && c.warning) fontColor = c.warning;
    else fontColor = 'inherit';

    // Margins (percentage of font-size) with safe defaults
    const { headings = { marginTop: 0.75, marginBottom: 0.35 }, paragraphs = { marginTop: 0, marginBottom: 0.6 } } =
        props.theme || {};

    const [value, unit] = getValueAndUnit(fontSize as any);
    const units = unit || 'px';
    const isHeadingTag = props.tag && (props.tag as string).indexOf('h') === 0;
    const mtFactor = isHeadingTag ? headings.marginTop : paragraphs.marginTop;
    const mbFactor = isHeadingTag ? headings.marginBottom : paragraphs.marginBottom;
    const marginTop = `${Math.ceil((Number(value) || 0) * mtFactor)}${units}`;
    const marginBottom = `${Math.ceil((Number(value) || 0) * mbFactor)}${units}`;

    return css`
        font-size: ${fontSize};
        font-weight: ${fontWeight};
        line-height: ${lineHeight};
        color: ${fontColor};
        margin-bottom: ${marginBottom};
        margin-top: ${marginTop};
    `;
};

const Text = styled(BaseText)<TextProps>`
    line-height: ${({ theme }) => theme?.lineHeights?.copy ?? 1.4};
    margin-top: 0;

    ${props => getTextStyle(null, props)}

    ${props => isNotInlineTag(props) && breakpointUp.xs`${getTextStyle('xs', props)}`}
  ${props => isNotInlineTag(props) && breakpointUp.sm`${getTextStyle('sm', props)}`}
  ${props => isNotInlineTag(props) && breakpointUp.md`${getTextStyle('md', props)}`}
  ${props => isNotInlineTag(props) && breakpointUp.lg`${getTextStyle('lg', props)}`}
  ${props => isNotInlineTag(props) && breakpointUp.xl`${getTextStyle('xl', props)}`}

  ${space}
  ${layout}
  ${typography}
  ${color}
  ${position}
`;

Text.displayName = 'Text';

export { Text as StyledText }; // optional named export, if you rely on it elsewhere
export default Text;
